import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../lib/api';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, AlertTriangle, CheckCircle, Clock, Copy, Check } from 'lucide-react';

const IMPACT_COLORS = { High: '#EF4444', Med: '#DDA73D', Low: '#22C55E' };
const EFFORT_LABELS = { S: 'Small (< 1 hour)', M: 'Medium (1-4 hours)', L: 'Large (4+ hours)' };
const CATEGORY_LABELS = {
  gbp_readiness: 'Google Business Profile',
  website_relevance: 'Website Relevance',
  technical_seo: 'Technical SEO',
  prominence: 'Prominence & Reputation',
  risk_compliance: 'Risk & Compliance'
};

export default function AuditDetailPage() {
  const { issueId } = useParams();
  const [issue, setIssue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);

  useEffect(() => {
    api.get(`/issues/${issueId}`).then(r => setIssue(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [issueId]);

  const copySnippet = () => {
    if (issue?.example_snippet) {
      navigator.clipboard.writeText(issue.example_snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const toggleStatus = async () => {
    if (!issue) return;
    setStatusUpdating(true);
    const newStatus = issue.status === 'open' ? 'fixed' : 'open';
    try {
      await api.patch(`/issues/${issueId}`, { status: newStatus });
      setIssue(prev => ({ ...prev, status: newStatus }));
    } catch {} finally {
      setStatusUpdating(false);
    }
  };

  if (loading) return <div className="min-h-screen bg-[#F7F9FC]"><Navbar /><div className="max-w-3xl mx-auto px-4 py-12 animate-pulse"><div className="h-8 bg-[#EFF3F8] rounded w-2/3 mb-4" /><div className="h-4 bg-[#EFF3F8] rounded w-1/2" /></div></div>;
  if (!issue) return <div className="min-h-screen bg-[#F7F9FC]"><Navbar /><div className="max-w-3xl mx-auto px-4 py-12 text-center text-[#64748B]">Issue not found</div></div>;

  return (
    <div className="min-h-screen bg-[#F7F9FC]">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <Link to={`/projects/${issue.project_id}`} data-testid="back-to-project" className="inline-flex items-center gap-1 text-sm text-[#64748B] hover:text-[#0268C4] mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Project
        </Link>

        <div className="bg-white rounded-2xl border border-[#E2E8F0] shadow-[0_4px_24px_rgba(44,48,46,0.04)] overflow-hidden">
          {/* Header */}
          <div className="p-6 sm:p-8 border-b border-[#E2E8F0]">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="outline" className="text-xs rounded-lg border-[#E2E8F0] text-[#64748B]">{CATEGORY_LABELS[issue.category] || issue.category}</Badge>
                  <Badge variant="outline" className="text-xs rounded-lg border-[#E2E8F0]" style={{ color: IMPACT_COLORS[issue.impact] }}>{issue.impact} Impact</Badge>
                </div>
                <h1 data-testid="issue-title" className="font-heading text-xl sm:text-2xl font-medium tracking-tight text-[#0F2951]">{issue.title}</h1>
              </div>
              <Button
                data-testid="toggle-status-button"
                variant="outline"
                onClick={toggleStatus}
                disabled={statusUpdating}
                className={`rounded-xl border-[#E2E8F0] ${issue.status === 'fixed' ? 'text-[#22C55E] bg-[#22C55E]/10' : 'text-[#64748B]'}`}
              >
                {issue.status === 'fixed' ? <><CheckCircle className="w-4 h-4 mr-1.5" /> Fixed</> : <><Clock className="w-4 h-4 mr-1.5" /> Mark Fixed</>}
              </Button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 sm:p-8 space-y-6">
            {/* What's wrong */}
            <div>
              <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[#64748B] mb-2">What's Wrong</h3>
              <p className="text-base text-[#0F2951] leading-relaxed">{issue.description}</p>
            </div>

            {/* Why it matters */}
            {issue.why_it_matters && (
              <div className="p-4 bg-[#EFF3F8] rounded-xl border border-[#E2E8F0]">
                <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[#64748B] mb-2">Why It Matters</h3>
                <p className="text-sm text-[#0F2951] leading-relaxed">{issue.why_it_matters}</p>
              </div>
            )}

            {/* Fix instructions */}
            {issue.fix_instructions && (
              <div>
                <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[#64748B] mb-2">How to Fix</h3>
                <div className="text-sm text-[#0F2951] leading-relaxed whitespace-pre-line">{issue.fix_instructions}</div>
              </div>
            )}

            {/* Example snippet */}
            {issue.example_snippet && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[#64748B]">Example Snippet</h3>
                  <Button data-testid="copy-snippet-button" variant="ghost" size="sm" onClick={copySnippet} className="text-[#64748B] hover:text-[#0268C4] rounded-lg">
                    {copied ? <Check className="w-3.5 h-3.5 mr-1" /> : <Copy className="w-3.5 h-3.5 mr-1" />}
                    {copied ? 'Copied' : 'Copy'}
                  </Button>
                </div>
                <pre className="bg-[#0F2951] text-[#F7F9FC] p-4 rounded-xl text-xs overflow-x-auto">{issue.example_snippet}</pre>
              </div>
            )}

            {/* Risk warnings */}
            {issue.risk_warnings && (
              <div className="p-4 bg-[#EF4444]/10 rounded-xl border border-[#EF4444]/20">
                <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[#EF4444] mb-2 flex items-center gap-1">
                  <AlertTriangle className="w-3.5 h-3.5" /> Risk Warning
                </h3>
                <p className="text-sm text-[#0F2951]">{issue.risk_warnings}</p>
              </div>
            )}

            {/* Meta */}
            <div className="flex flex-wrap gap-4 pt-4 border-t border-[#E2E8F0]">
              <div>
                <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#64748B]">Effort</span>
                <p className="text-sm text-[#0F2951] mt-0.5">{EFFORT_LABELS[issue.effort] || issue.effort}</p>
              </div>
              <div>
                <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#64748B]">Impact</span>
                <p className="text-sm text-[#0F2951] mt-0.5" style={{ color: IMPACT_COLORS[issue.impact] }}>{issue.impact}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
